'use client'

import { HubspotProvider } from '@aaronhayes/react-use-hubspot-form'
import * as Sentry from '@sentry/nextjs'
import { LDEvaluationDetail, withLDProvider } from 'launchdarkly-react-client-sdk'
import { DefaultSeo } from 'next-seo'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { ComponentType, Fragment, useEffect } from 'react'
import { ToastProvider } from 'react-toast-notifications'

import '@fortawesome/fontawesome-svg-core/styles.css'
import * as snippet from '@segment/snippet'
import NProgress from 'nprogress'
import { useTheme } from 'store/theme'
import 'styles/globals.css'
import { SWRConfig } from 'swr'

import '@infura/packages.tailwind/dist/styles.css'

import CustomToast from 'components/CustomToast'
import GoogleTagManager from 'components/GoogleTagManager'
import Layout from 'components/Layout'
import PageTracker from 'components/PageTracker'
import UserIdentifier from 'components/UserIdentifier'
import {
  ModalStateProvider,
  OutstandingInvoicesModal
} from 'components/billing/OutstandingInvoicesModal/OutstandingInvoicesModal'
import { SelectedApiKeyContextProvider } from 'components/keySelection/Context'
import FirstRequestTourProvider from 'components/onboardingFirstRequestTour/FirstRequestTourContext'
import { WalletProvider } from 'components/wallet/WalletProvider'

import { BurstableThroughputAddonBanner } from 'components/burstableThroughputAddon/BurstableThroughputAddonBanner'
import { SurveyBanner } from 'components/ipfs/SurveyBanner'
import SEO from 'lib/seo'

export const appEnvironment = {
  isDevelopment: process.env.ENVIRONMENT === 'development',
  isProduction: process.env.ENVIRONMENT === 'production',
  isTest: process.env.ENVIRONMENT === 'test'
}

if (appEnvironment.isTest) {
  require('../mocks')
}

const OsanoScript = () => (
  <Script
    id='OsanoScript'
    src='https://cmp.osano.com/AzZMxHTbQDOQD8c1J/a2e89f0e-f467-4542-bfea-30ea2c1a6648/osano.js'
    strategy='beforeInteractive'
  />
)

const ChargifyScript = () => {
  return <Script src='https://js.chargify.com/latest/chargify.js' />
}

const AccessibeScript = () => {
  if (!appEnvironment.isProduction) return <></>
  return (
    <Script async id='accessibe' strategy={'afterInteractive'}>
      {`(function(){ var s = document.createElement('script'); var h = document.querySelector('head') || document.body; s.src = 'https://acsbapp.com/apps/app/dist/js/app.js'; s.async = true; s.onload = function(){ acsbJS.init({ statementLink : '', footerHtml : '', hideMobile : false, hideTrigger : false, disableBgProcess : false, language : 'en', position : 'right', leadColor : '#146FF8', triggerColor : '#146FF8', triggerRadius : '50%', triggerPositionX : 'right', triggerPositionY : 'bottom', triggerIcon : 'people', triggerSize : 'bottom', triggerOffsetX : 20, triggerOffsetY : 20, mobile : { triggerSize : 'small', triggerPositionX : 'right', triggerPositionY : 'bottom', triggerOffsetX : 20, triggerOffsetY : 20, triggerRadius : '20' } }); }; h.appendChild(s); })();`}
    </Script>
  )
}

const TrackingScripts = () => {
  if (!appEnvironment.isProduction) return <></>
  return (
    <>
      <GoogleTagManager />
      <PageTracker />
      <Script dangerouslySetInnerHTML={{ __html: renderSnippet() }} id='segment-script' />
    </>
  )
}

function renderSnippet() {
  const opts = {
    apiKey: process.env.SEGMENT_WRITE_KEY,
    // note: the page option only covers SSR tracking.
    // Page.js is used to track other events using `window.analytics.page()`
    page: true
  }

  if (process.env.NODE_ENV === 'development') {
    return snippet.max(opts)
  }

  return snippet.min(opts)
}

function _App({ Component, pageProps }) {
  const router = useRouter()
  const theme = useTheme()

  useEffect(() => {
    router.events.on('routeChangeStart', () => NProgress.start())
    router.events.on('routeChangeComplete', () => NProgress.done())
    router.events.on('routeChangeError', () => NProgress.done())
  }, [])

  useEffect(() => {
    try {
      const localTheme = JSON.parse(localStorage.getItem('infura-theme') || '')
      if (localTheme) {
        document.body.setAttribute('data-theme', theme)
      }
    } catch (err) {
      document.body.setAttribute('data-theme', theme)
    }
  }, [theme])

  return (
    <>
      <Head>
        <meta content='width=device-width, initial-scale=1, maximum-scale=1' name='viewport' />
      </Head>
      <ChargifyScript />
      <DefaultSeo {...SEO} />
      <OsanoScript />
      <AccessibeScript />
      <TrackingScripts />
      <SWRConfig
        value={{
          errorRetryCount: 2,
          shouldRetryOnError: true
        }}
      >
        <Fragment>
          <UserIdentifier appEnvironment={appEnvironment} />
          <HubspotProvider addToHead async>
            <ToastProvider components={{ Toast: CustomToast }} placement='top-right'>
              <ModalStateProvider>
                <WalletProvider>
                  {Component._noLayout ? (
                    <Component {...pageProps} />
                  ) : (
                    <SelectedApiKeyContextProvider>
                      <Layout>
                        <OutstandingInvoicesModal />
                        <SurveyBanner />
                        <BurstableThroughputAddonBanner />
                        <FirstRequestTourProvider>
                          <Component {...pageProps} />
                        </FirstRequestTourProvider>
                      </Layout>
                    </SelectedApiKeyContextProvider>
                  )}
                </WalletProvider>
              </ModalStateProvider>
            </ToastProvider>
          </HubspotProvider>
        </Fragment>
      </SWRConfig>
    </>
  )
}

export default withLDProvider({
  clientSideID: process.env.LD_CLIENT_ID as string,
  context: {
    anonymous: true,
    key: 'ld-anonymous-user-key',
    kind: 'user'
  },
  deferInitialization: true,
  options: {
    allAttributesPrivate: true,
    bootstrap: 'localStorage',
    inspectors: [
      {
        method: (key: string, detail: LDEvaluationDetail) => {
          try {
            Sentry.setTag(key, detail.value)
          } catch {}

          try {
            ;(window as any).rum?.addFeatureFlagEvaluation(key, detail.value)
          } catch {}
        },
        name: 'dd-inspector',
        type: 'flag-used'
      }
    ]
  }
})(_App as ComponentType<{}>)
